import { useState, useContext, useEffect } from "react";
import { useFormik, Form, FormikProvider } from "formik";
import * as Yup from "yup";
import {
  Grid,
  Button,
  Dialog,
  TextField,
  Typography,
  DialogContent,
  Select,
  MenuItem,
  FormControl,
  Checkbox,
  Stack,
} from "@mui/material";
//Import from project
import "../../Style/dialogstyle.scss";
import { useMutation } from "@apollo/client";
import { AuthContext } from "../../Context/AuthContext";
import { DialogTitleComp } from "../../Utils/Component";
import {
  CREATE_CAPITAL_PROVINCE,
  UPDATE_CAPITAL_PROVINCE,
} from "../../Schema/CapitalProvince";

export default function CapitalProvinceForm({
  open,
  editData,
  setRefetch,
  dialogTitle,
  handleClose,
  hideBackdrop,
}) {
  // Change Language
  const { t, setAlert } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  // ================================= End upload Image ===============================

  const [createCapitalProvince] = useMutation(CREATE_CAPITAL_PROVINCE, {
    onCompleted: ({ createCapitalProvince }) => {
      setLoading(false);
      if (createCapitalProvince?.isSuccess === true) {
        setAlert(true, "success", createCapitalProvince?.message);
        handleClose();
        resetForm();
        setRefetch();
      } else {
        setAlert(true, "error", createCapitalProvince?.message);
      }
    },
    onError: ({ message }) => {
      setLoading(false);
      setAlert(true, "error", { messageKh: message, messageEn: message });
    },
  });

  const [updateCapitalProvince] = useMutation(UPDATE_CAPITAL_PROVINCE, {
    onCompleted: ({ updateCapitalProvince }) => {
      setLoading(false);
      if (updateCapitalProvince?.isSuccess === true) {
        setAlert(true, "success", updateCapitalProvince?.message);
        handleClose();
        resetForm();
        setRefetch();
      } else {
        setAlert(true, "error", updateCapitalProvince?.message);
      }
    },
    onError: ({ message }) => {
      setLoading(false);
      setAlert(true, "error", { messageKh: message, messageEn: message });
    },
  });

  //fromik
  const CheckValidation = Yup.object().shape({
    isDefault: Yup.string().required(t("required")),
    capital_province_type: Yup.string().required(t("required")),
    capital_province_code: Yup.string().required(t("required")),
    capital_province_name_en: Yup.string().required(t("required")),
    capital_province_name_kh: Yup.string().required(t("required")),
  });

  const formik = useFormik({
    initialValues: {
      isDefault: false,
      capital_province_type: "province",
      capital_province_code: "",
      capital_province_name_en: "",
      capital_province_name_kh: "",
    },

    validationSchema: CheckValidation,
    onSubmit: async (values) => {
      setLoading(true);
      if (dialogTitle === "Create") {
        createCapitalProvince({
          variables: {
            input: {
              ...values,
            },
          },
        });
      } else {
        updateCapitalProvince({
          variables: {
            id: editData?._id,
            input: {
              ...values,
            },
          },
        });
      }
    },
  });

  const {
    values,
    errors,
    touched,
    handleSubmit,
    setFieldValue,
    getFieldProps,
    resetForm,
  } = formik;

  useEffect(() => {
    if (editData) {
      setFieldValue("isDefault", editData?.isDefault);
      setFieldValue("capital_province_type", editData?.capital_province_type);
      setFieldValue("capital_province_code", editData?.capital_province_code);
      setFieldValue("capital_province_name_en", editData?.capital_province_name_en);
      setFieldValue("capital_province_name_kh", editData?.capital_province_name_kh);
    }
  }, [editData, open]);

  return (
    <Dialog
      fullWidth
      open={open}
      maxWidth="xs"
      className="dialog-container"
      hideBackdrop={hideBackdrop}
    >
      <DialogTitleComp
        title={
          dialogTitle === "Create"
            ? t("modal_title_create_capital_province")
            : t("modal_title_update_capital_province")
        }
        classTitle={"dialog-title"}
        classIcon={"close-icon"}
        handleClose={handleClose}
      />

      <DialogContent dividers>
        <FormikProvider value={formik}>
          <Form noValidate autoComplete="off" onSubmit={handleSubmit}>
            <Grid container rowSpacing={2} columnSpacing={3}>
              <Grid item xs={12} sm={6}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_code")}
                </Typography>
                <TextField
                  className="text-field"
                  size="small"
                  fullWidth
                  placeholder={t("thead_code")}
                  {...getFieldProps("capital_province_code")}
                  error={Boolean(
                    touched.capital_province_code &&
                    errors.capital_province_code
                  )}
                  helperText={
                    touched.capital_province_code &&
                    errors.capital_province_code
                  }
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_type")}
                </Typography>
                <FormControl fullWidth className="search-field" size="small">
                  <Select {...getFieldProps("capital_province_type")}>
                    <MenuItem value={"province"}>
                      {t("thead_province")}
                    </MenuItem>
                    <MenuItem value={"capital"}>{t("thead_capital")}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_name")}(KH)
                </Typography>
                <TextField
                  className="text-field"
                  size="small"
                  fullWidth
                  placeholder={t("thead_name")}
                  {...getFieldProps("capital_province_name_kh")}
                  error={Boolean(
                    touched.capital_province_name_kh &&
                    errors.capital_province_name_kh
                  )}
                  helperText={
                    touched.capital_province_name_kh &&
                    errors.capital_province_name_kh
                  }
                />
              </Grid>

              <Grid item xs={12}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_name")}(ENG)
                </Typography>
                <TextField
                  className="text-field"
                  size="small"
                  fullWidth
                  placeholder={t("thead_name")}
                  {...getFieldProps("capital_province_name_en")}
                  error={Boolean(
                    touched.capital_province_name_en &&
                    errors.capital_province_name_en
                  )}
                  helperText={
                    touched.capital_province_name_en &&
                    errors.capital_province_name_en
                  }
                />
              </Grid>

              <Grid item xs={12}>
                <Stack direction="row" alignItems="center" >
                  <Checkbox
                    checked={values?.isDefault}
                    onChange={({ target }) => setFieldValue("isDefault", Boolean(target?.checked))}
                  />
                  <Typography className={t("class-name-field-title")} sx={{ marginTop: 1 }}>
                    {t("thead_default")}
                  </Typography>
                </Stack>
              </Grid>

              <Grid item xs={12}>
                {loading ? (
                  <Button className={t("class-btn-create")} fullWidth>
                    {t("loading")}
                  </Button>
                ) : (
                  <Button
                    className={t("class-btn-create")}
                    fullWidth
                    onClick={handleSubmit}
                  >
                    {dialogTitle === "Create" ? t("create") : t("update")}
                  </Button>
                )}
              </Grid>
            </Grid>
          </Form>
        </FormikProvider>
      </DialogContent>
    </Dialog>
  );
}
